/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-throw-literal */
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AppContext, AuthContext, initialAlertState } from "../../../store";
import { useAlertContext, useDropdownApis } from "../../../shared";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import { getListById, updateExcel, uploadExcel } from "../api/ListApis";
import { errorMessage, successMessage } from "../../../utils";
import { Button, Stack } from "@mui/material";

export const useManageAccountFileUpload = () => {
  const location = useLocation();
  const { id } = useParams();
  const isStoreAdmin = location.pathname.includes("/store-admin");

  const [, forceUpdate] = React.useState();
  const { appState } = React.useContext(AppContext);
  const { globalData } = appState;
  const { auth } = React.useContext(AuthContext);
  const { setAlert } = useAlertContext();
  const navigate = useNavigate();

  const [state, setState] = useImmer({
    data: {
      store_id:
        isStoreAdmin && auth?.data?.selectedStoreId
          ? auth.data.selectedStoreId
          : "",
      month: "",
      year: "",
      excel_file: "",
    },
    isFormUpdated: true,
    uploadButtonLoading: false,
    uploadsFormLoading: id ? true : false,
  });

  const { storeList } = useDropdownApis({
    isStoreList: !isStoreAdmin,
  });

  const formValidator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => message,
      validators: {
        IconFormat: {
          message: "The file should be an Excel file (.xlsx or .xls)",
          rule: (val) => {
            if (val) {
              const fileName = val.name ? val.name : val;
              const extension = fileName.split(".").pop().toLowerCase();
              return ["xlsx", "xls"].includes(extension);
            }
            return false;
          },
          required: true,
        },
      },
    })
  );

  //=============================================================================== API FUNCTIONS ==============================================================================
  const handleUploadExcel = async () => {
    triggerUploadButtonLoading(true);
    try {
      let formData = new FormData();
      Object.keys(state.data).forEach((key) => {
        if (key !== "excel_file") {
          formData.append(key, state.data[key]);
        }
      });
      formData.append("excel_file", state.file);

      const response = await uploadExcel(formData);

      const { success, message } = response;
      if (success) {
        successMessage(
          message ?? "PL & BS Account file uploaded successfully."
        );

        navigate(isStoreAdmin ? "/store-admin/list" : "/list");
      } else {
        throw {
          response: {
            data: { message },
          },
        };
      }
    } catch (err) {
      const message = err.response.data.message ?? "Failed to upload the file";
      errorMessage(message);
    } finally {
      triggerUploadButtonLoading(false);
    }
  };

  const handleUpdateUploadExcel = async (detailsId) => {
    triggerUploadButtonLoading(true);

    try {
      let formData = new FormData();

      state.file && formData.append("excel_file", state.file);
      formData.append("store_id", state.data.store_id);
      formData.append("month", state.data.month);
      formData.append("year", state.data.year);
      formData.append("_method", "put");

      const response = await updateExcel(formData, detailsId);

      const { success, message } = response;
      if (success) {
        successMessage(message);
        const navigateUrl = isStoreAdmin ? "/store-admin/list" : "/list";
        navigate(navigateUrl);
      } else {
        throw {
          response: { data: { message } },
        };
      }
    } catch (err) {
      const message = err.response.data.message ?? "Account upload failed";
      errorMessage(message);
    } finally {
      triggerUploadButtonLoading(false);
    }
  };

  const getListDetails = async (detailsId) => {
    triggerUploadsFormLoading(true);
    try {
      const response = await getListById(detailsId);
      const { success, data, message } = response;
      if (success) {
        setState((draft) => {
          draft.data = {
            ...data,
            store_id: data?.store_id,
            month: data?.month,
            year: data?.year,
            file: data?.excel_file,
          };
        });
      } else {
        throw {
          response: { data: { message } },
        };
      }
    } catch (err) {
      const message =
        err.response.data.message ?? "Failed to fetch upload details";
      errorMessage(message);
    } finally {
      triggerUploadsFormLoading(false);
    }
  };

  //============================================================================== UTIL FUNCTIONS ==============================================================================
  const triggerUploadsFormLoading = (loading) => {
    setState((draft) => {
      draft.uploadsFormLoading = loading;
    });
  };

  const triggerUploadButtonLoading = (loading) => {
    setState((draft) => {
      draft.uploadButtonLoading = loading;
    });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.data[name] = value;
    });
  };


  const handleFileChange = (file) => {
    setState((draft) => {
      draft.file = file;
      draft.isFormUpdated = false;
    });
  };

  const resetSelectedFile = () => {
    setState((draft) => {
      draft.file = "";
      draft.isFormUpdated = true;
    });
  };

  const handleCloseAlert = () => setAlert(initialAlertState);

  const onUploadAccountFile = async () => {
    if (formValidator.current.allValid()) {
      if (id) {
        const selectedStore = !isStoreAdmin
          ? storeList.find((store) => store.id === state.data.store_id)
          : "";

        const alertMessage = isStoreAdmin
          ? `The account file for ${state.data.month} ${state.data.year} has already been uploaded. Would you like to request an overwrite?`
          : `The Account file for the store ${selectedStore.name} for ${state.data.month} ${state.data.year} has already been uploaded. Would you like to overwrite it?`;
        setAlert((draft) => {
          draft.open = true;
          draft.title = "Alert";
          draft.description = alertMessage;
          draft.rowAction = (
            <Stack flexDirection="row" gap="12px">
              <Button
                onClick={() => {
                  handleCloseAlert();
                  resetSelectedFile();
                }}
              >
                No
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  handleCloseAlert();
                  await handleUpdateUploadExcel(id);
                }}
              >
                Yes
              </Button>
            </Stack>
          );
          draft.sx = { width: 425 };
        });
      } else {
        handleUploadExcel();
      }
    } else {
      formValidator.current.showMessages();
      forceUpdate(1);
    }
  };

  React.useEffect(() => {
    if (id) {
      getListDetails(id);
    }
  }, []);

  return {
    state,
    formValidator,
    storeList,
    sample_import_file: globalData?.pl_bs_reports?.sample_import_file,
    pl_bs_accounts_months: globalData?.pl_bs_reports?.pl_bs_accounts_months,
    pl_bs_accounts_years: globalData?.pl_bs_reports?.pl_bs_accounts_years,
    handleFormChange,
    handleFileChange,
    onUploadAccountFile,
  };
};
